import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import {
  Box, Typography, TextField, Avatar, Badge, Stack, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete,
  Chip, Divider, CircularProgress, Menu, MenuItem, useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import ForumIcon from '@mui/icons-material/Forum';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { chatAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SOCKET_URL = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

const ROLE_COLORS = {
  admin: '#FF6B35', support_team: '#4472C4', team_member: '#2E7D32',
  qa_team: '#7B1FA2', higher_authority: '#C62828',
};

const formatTime = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
};
const formatDay = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return 'Today';
  return d.toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });
};

// WhatsApp-style delivery ticks for messages I sent:
//   sent = one grey tick · delivered = two grey ticks · read = two blue ticks
function MessageTicks({ status }) {
  const label = { sent: 'Sent', delivered: 'Delivered', read: 'Read' }[status] || 'Sent';
  const iconSx = { fontSize: 16 };
  return (
    <Tooltip title={label} placement="top">
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {status === 'read' ? (
          <DoneAllIcon sx={{ ...iconSx, color: '#34B7F1' }} />
        ) : status === 'delivered' ? (
          <DoneAllIcon sx={{ ...iconSx, color: 'text.disabled' }} />
        ) : (
          <DoneIcon sx={{ ...iconSx, color: 'text.disabled' }} />
        )}
      </Box>
    </Tooltip>
  );
}

// Highlights any "@Full Name" substrings that match a real participant —
// mirrors the same pattern used for Project Communication mentions.
function renderWithMentions(text, participantNames) {
  if (!text || !participantNames || participantNames.length === 0) return text;
  const sorted = [...participantNames].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`@(${sorted.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'g');
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <Box key={match.index} component="span" sx={{ fontWeight: 700, color: 'inherit', bgcolor: 'rgba(79,195,247,0.25)', borderRadius: 0.5, px: 0.4 }}>
        @{match[1]}
      </Box>
    );
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

export default function ChatPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const token = localStorage.getItem('token');

  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState(null); // conversation object
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [mentionQuery, setMentionQuery] = useState(null); // null = dropdown closed; string = active @query
  const [mentionIndex, setMentionIndex] = useState(0);

  const [allUsers, setAllUsers] = useState([]);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [dmTarget, setDmTarget] = useState(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [addMemberPick, setAddMemberPick] = useState(null);

  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const selectedRef = useRef(null);
  selectedRef.current = selected;
  const messagesRef = useRef([]);
  messagesRef.current = messages;

  // Applies a freshly fetched message list. Distinguishes "new messages arrived"
  // (mark the conversation read) from "only the ticks changed" (just re-render) so
  // receipt updates never trigger extra markRead calls.
  const syncMessages = (incoming, conv) => {
    const prev = messagesRef.current;
    const idsChanged = incoming.length !== prev.length || incoming.some((m, i) => m.id !== prev[i]?.id);
    const statusChanged = !idsChanged && incoming.some((m, i) => m.status !== prev[i]?.status);
    if (!idsChanged && !statusChanged) return;
    setMessages(incoming);
    if (idsChanged) {
      const last = incoming[incoming.length - 1];
      if (last && last.sender_id !== user?.id) chatAPI.markRead(conv.id).catch(() => {});
    }
  };

  const refreshMessages = () => {
    const current = selectedRef.current;
    if (!current) return;
    chatAPI.getMessages(current.id)
      .then(res => { if (selectedRef.current?.id === current.id) syncMessages(res.data || [], current); })
      .catch(() => {});
  };

  const isAdmin = user?.role === 'admin';

  // ─── Load conversations + users once ───────────────────────────────────────
  const loadConversations = useCallback(() => {
    chatAPI.getConversations()
      .then(res => setConversations(res.data || []))
      .catch(() => showToast('Failed to load conversations', 'error'))
      .finally(() => setLoadingList(false));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadConversations();
    chatAPI.getChatUsers().then(res => setAllUsers(res.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // ─── Socket connection ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    const socket = io(SOCKET_URL, { auth: { token }, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('chat:message', (msg) => {
      const current = selectedRef.current;
      if (current && msg.conversation_id === current.id) {
        setMessages(prev => (prev.some(m => m.id === msg.id) ? prev : [...prev, msg]));
        if (msg.sender_id !== user?.id) chatAPI.markRead(current.id).catch(() => {});
      }
      // Refresh the list so previews/unread counts/ordering stay current.
      loadConversations();
    });

    // Someone read/received messages in a conversation — refresh so ticks update instantly.
    socket.on('chat:receipts', ({ conversationId }) => {
      if (selectedRef.current?.id === conversationId) refreshMessages();
    });

    socket.on('chat:membersUpdated', ({ conversationId }) => {
      loadConversations();
      if (selectedRef.current?.id === conversationId) {
        chatAPI.getConversations().then(res => {
          const updated = (res.data || []).find(c => c.id === conversationId);
          if (updated) setSelected(updated);
        }).catch(() => {});
      }
    });

    return () => socket.disconnect();
    // eslint-disable-next-line
  }, [token]);

  // ─── Polling fallback ────────────────────────────────────────────────────────
  // Socket.io delivers messages instantly when it can, but a reverse proxy
  // that isn't configured to upgrade WebSocket connections (a common Nginx
  // gotcha) will silently swallow the real-time push with no error — the
  // message still sends and saves fine, it just never arrives live on the
  // other end until something re-fetches. This polling loop is a safety net
  // so the chat stays current either way, without needing a manual reload.
  useEffect(() => {
    const interval = setInterval(() => {
      loadConversations();
      refreshMessages();
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  // ─── Select a conversation ──────────────────────────────────────────────────
  const openConversation = (conv) => {
    if (selected?.id) socketRef.current?.emit('chat:leave', selected.id);
    setSelected(conv);
    setDraft('');
    setMentionQuery(null);
    setLoadingMessages(true);
    socketRef.current?.emit('chat:join', conv.id);
    chatAPI.getMessages(conv.id)
      .then(res => setMessages(res.data || []))
      .catch(() => showToast('Failed to load messages', 'error'))
      .finally(() => setLoadingMessages(false));
    if (conv.unread_count > 0) {
      chatAPI.markRead(conv.id).then(loadConversations).catch(() => {});
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── Send message ───────────────────────────────────────────────────────────
  const handleSend = () => {
    const text = draft.trim();
    if (!text || !selected) return;
    setDraft('');
    setMentionQuery(null);
    chatAPI.sendMessage(selected.id, text)
      .then(res => {
        setMessages(prev => (prev.some(m => m.id === res.data.id) ? prev : [...prev, res.data]));
        loadConversations();
      })
      .catch(() => showToast('Failed to send message', 'error'));
  };

  // ─── @Mentions ───────────────────────────────────────────────────────────────
  // Detects an in-progress "@partial" token at the end of the draft (the
  // common single-line chat-input pattern) and opens a suggestion dropdown
  // of the current conversation's participants that match it.
  const mentionCandidates = selected
    ? (selected.participants || [])
        .filter(p => p.id !== user?.id)
        .filter(p => (p.name || '').toLowerCase().includes((mentionQuery || '').toLowerCase()))
    : [];

  const handleDraftChange = (value) => {
    setDraft(value);
    const match = value.match(/(?:^|\s)@(\w*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const handlePickMention = (person) => {
    setDraft(prev => prev.replace(/(?:^|\s)@(\w*)$/, (whole) => `${whole.startsWith(' ') ? ' ' : ''}@${person.name} `));
    setMentionQuery(null);
  };

  const handleDraftKeyDown = (e) => {
    if (mentionQuery !== null && mentionCandidates.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => (i + 1) % mentionCandidates.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => (i - 1 + mentionCandidates.length) % mentionCandidates.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handlePickMention(mentionCandidates[mentionIndex]); return; }
      if (e.key === 'Escape') { setMentionQuery(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ─── New direct chat ─────────────────────────────────────────────────────────
  const [startingChat, setStartingChat] = useState(false);
  const handleStartDirect = () => {
    if (!dmTarget || startingChat) return; // guards against a fast double-click firing two create requests
    setStartingChat(true);
    chatAPI.createDirect(dmTarget.id)
      .then(res => {
        setNewChatOpen(false);
        setDmTarget(null);
        loadConversations();
        // res.data is the raw Conversation row; give the thread pane just
        // enough shape to open immediately, the next list refresh fills in the rest.
        openConversation({ id: res.data.id, type: 'direct', name: dmTarget.name, other_user: dmTarget, participants: [], unread_count: 0 });
      })
      .catch(err => showToast(err.response?.data?.message || 'Failed to start chat', 'error'))
      .finally(() => setStartingChat(false));
  };

  // ─── New group (admin only) ─────────────────────────────────────────────────
  const [creatingGroup, setCreatingGroup] = useState(false);
  const handleCreateGroup = () => {
    if (!groupName.trim()) return showToast('Group name is required', 'error');
    if (creatingGroup) return;
    setCreatingGroup(true);
    chatAPI.createGroup({ name: groupName.trim(), member_ids: groupMembers.map(m => m.id) })
      .then(res => {
        setNewGroupOpen(false);
        setGroupName('');
        setGroupMembers([]);
        loadConversations();
        openConversation({ id: res.data.id, type: 'group', name: res.data.name, participants: [], unread_count: 0, my_role: 'admin' });
      })
      .catch(err => showToast(err.response?.data?.message || 'Failed to create group', 'error'))
      .finally(() => setCreatingGroup(false));
  };

  // ─── Group member management ────────────────────────────────────────────────
  const handleAddMember = () => {
    if (!addMemberPick || !selected) return;
    chatAPI.addMembers(selected.id, [addMemberPick.id])
      .then(() => {
        setAddMemberPick(null);
        loadConversations();
        chatAPI.getConversations().then(res => {
          const updated = (res.data || []).find(c => c.id === selected.id);
          if (updated) setSelected(updated);
        });
      })
      .catch(err => showToast(err.response?.data?.message || 'Failed to add member', 'error'));
  };
  const handleRemoveMember = (userId) => {
    if (!selected) return;
    chatAPI.removeMember(selected.id, userId)
      .then(() => {
        loadConversations();
        chatAPI.getConversations().then(res => {
          const updated = (res.data || []).find(c => c.id === selected.id);
          if (updated) setSelected(updated);
        });
      })
      .catch(err => showToast(err.response?.data?.message || 'Failed to remove member', 'error'));
  };

  const filteredConversations = conversations.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const groupMembersAvailable = allUsers.filter(u => !groupMembers.some(m => m.id === u.id));
  const addMemberAvailable = selected
    ? allUsers.filter(u => !(selected.participants || []).some(p => p.id === u.id))
    : [];

  // ─── Message list day-grouping ──────────────────────────────────────────────
  const grouped = [];
  messages.forEach((m) => {
    const day = formatDay(m.created_at);
    const last = grouped[grouped.length - 1];
    if (last && last.day === day) last.items.push(m);
    else grouped.push({ day, items: [m] });
  });

  return (
    <Box sx={{
      display: 'flex',
      // AppLayout's TopBar (60px, desktop only) + its content padding (24px
      // top+bottom desktop / 72px top+bottom mobile, to clear the fixed
      // mobile AppBar+BottomNav) — computed precisely so this panel never
      // overflows its scrollable parent and clips at the bottom.
      height: { xs: 'calc(100vh - 144px)', sm: 'calc(100vh - 108px)' },
      border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden',
      bgcolor: 'background.paper', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      {/* ── Conversation list ── */}
      <Box sx={{
        width: isMobile ? '100%' : 320, flexShrink: 0, borderRight: isMobile ? 'none' : '1px solid',
        borderColor: 'divider', display: isMobile && selected ? 'none' : 'flex', flexDirection: 'column',
      }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Chat</Typography>
            <Tooltip title="Start a new chat">
              <IconButton size="small" onClick={(e) => setAddMenuAnchor(e.currentTarget)} sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={addMenuAnchor} open={!!addMenuAnchor} onClose={() => setAddMenuAnchor(null)}>
              <MenuItem onClick={() => { setNewChatOpen(true); setAddMenuAnchor(null); }}>
                <PersonAddAlt1Icon fontSize="small" sx={{ mr: 1 }} /> New Direct Message
              </MenuItem>
              {isAdmin && (
                <MenuItem onClick={() => { setNewGroupOpen(true); setAddMenuAnchor(null); }}>
                  <GroupAddIcon fontSize="small" sx={{ mr: 1 }} /> New Group
                </MenuItem>
              )}
            </Menu>
          </Box>
          <TextField
            fullWidth size="small" placeholder="Search conversations..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.disabled' }} /> }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loadingList ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={24} /></Box>
          ) : filteredConversations.length === 0 ? (
            <Box sx={{ textAlign: 'center', pt: 6, px: 2 }}>
              <ForumIcon sx={{ fontSize: 40, opacity: 0.15, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">No conversations yet. Start one with the + button above.</Typography>
            </Box>
          ) : (
            filteredConversations.map((c) => (
              <Box key={c.id} onClick={() => openConversation(c)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, cursor: 'pointer',
                  bgcolor: selected?.id === c.id ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderBottom: '1px solid', borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 'none' },
                }}>
                <Badge badgeContent={c.unread_count} color="error" overlap="circular">
                  <Avatar sx={{
                    bgcolor: c.type === 'group' ? '#4472C420' : (ROLE_COLORS[c.other_user?.role] || '#888') + '30',
                    color: c.type === 'group' ? '#4472C4' : (ROLE_COLORS[c.other_user?.role] || '#888'),
                    fontWeight: 700,
                  }}>
                    {c.type === 'group' ? <GroupIcon fontSize="small" /> : (c.name || '?').charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: c.unread_count ? 800 : 600 }} noWrap>{c.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {c.last_message ? `${c.last_message.sender_id === user?.id ? 'You: ' : ''}${c.last_message.message}` : 'No messages yet'}
                  </Typography>
                </Box>
                {c.last_message && (
                  <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                    {formatTime(c.last_message.created_at)}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* ── Thread ── */}
      <Box sx={{ flex: 1, display: isMobile && !selected ? 'none' : 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!selected ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ForumIcon sx={{ fontSize: 56, opacity: 0.12 }} />
            <Typography color="text.secondary">Select a conversation to start chatting</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              {isMobile && (
                <IconButton size="small" onClick={() => setSelected(null)}><ArrowBackIcon fontSize="small" /></IconButton>
              )}
              <Avatar sx={{
                bgcolor: selected.type === 'group' ? '#4472C420' : (ROLE_COLORS[selected.other_user?.role] || '#888') + '30',
                color: selected.type === 'group' ? '#4472C4' : (ROLE_COLORS[selected.other_user?.role] || '#888'),
                fontWeight: 700,
              }}>
                {selected.type === 'group' ? <GroupIcon fontSize="small" /> : (selected.name || '?').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>{selected.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selected.type === 'group' ? `${(selected.participants || []).length} members` : 'Direct message'}
                </Typography>
              </Box>
              {selected.type === 'group' && (selected.my_role === 'admin' || isAdmin) && (
                <Button size="small" variant="outlined" onClick={() => setManageOpen(true)}>Manage</Button>
              )}
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {loadingMessages ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={24} /></Box>
              ) : messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                  No messages yet — say hello!
                </Typography>
              ) : (
                grouped.map((g, gi) => (
                  <Box key={gi} sx={{ mb: 2 }}>
                    <Divider sx={{ mb: 2 }}>
                      <Chip label={g.day} size="small" sx={{ fontSize: '0.65rem', fontWeight: 700 }} />
                    </Divider>
                    <Stack spacing={1.2}>
                      {g.items.map((m) => {
                        const mine = m.sender_id === user?.id;
                        return (
                          <Box key={m.id} sx={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', gap: 1, alignItems: 'flex-end' }}>
                            {!mine && (
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: (ROLE_COLORS[m.sender?.role] || '#888') + '30', color: ROLE_COLORS[m.sender?.role] || '#888' }}>
                                {m.sender?.name?.charAt(0)}
                              </Avatar>
                            )}
                            <Box sx={{ maxWidth: '70%' }}>
                              {!mine && selected.type === 'group' && (
                                <Typography variant="caption" sx={{ fontWeight: 700, ml: 0.5 }}>{m.sender?.name}</Typography>
                              )}
                              <Box sx={{
                                px: 1.5, py: 1, borderRadius: 2,
                                bgcolor: mine ? 'primary.main' : 'action.hover',
                                color: mine ? '#fff' : 'text.primary',
                                borderTopRightRadius: mine ? 4 : 2, borderTopLeftRadius: mine ? 2 : 4,
                              }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {renderWithMentions(m.message, (selected.participants || []).map(p => p.name))}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.disabled"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                                {formatTime(m.created_at)}
                                {mine && <MessageTicks status={m.status} />}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ))
              )}
              <div ref={messagesEndRef} />
            </Box>

            <Box sx={{ position: 'relative', display: 'flex', gap: 1, p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              {mentionQuery !== null && mentionCandidates.length > 0 && (
                <Box sx={{
                  position: 'absolute', bottom: '100%', left: 12, mb: 0.5, width: 260, maxHeight: 200, overflowY: 'auto',
                  bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 10,
                }}>
                  {mentionCandidates.map((p, i) => (
                    <Box key={p.id} onClick={() => handlePickMention(p)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer',
                        bgcolor: i === mentionIndex ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: (ROLE_COLORS[p.role] || '#888') + '30', color: ROLE_COLORS[p.role] || '#888' }}>
                        {p.name?.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <TextField
                fullWidth size="small" placeholder="Write a message... (@ to mention)"
                value={draft} onChange={(e) => handleDraftChange(e.target.value)}
                onKeyDown={handleDraftKeyDown}
                InputProps={{ startAdornment: <AlternateEmailIcon sx={{ fontSize: 15, mr: 0.5, color: 'text.disabled', display: { xs: 'none', sm: 'block' } }} /> }}
              />
              <IconButton color="primary" onClick={handleSend} disabled={!draft.trim()} sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}>
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </>
        )}
      </Box>

      {/* ── New Direct Message dialog ── */}
      <Dialog open={newChatOpen} onClose={() => setNewChatOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Direct Message</DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1 }}
            options={allUsers}
            getOptionLabel={(o) => o.name || ''}
            value={dmTarget}
            onChange={(_, val) => setDmTarget(val)}
            renderInput={(params) => <TextField {...params} label="Select a person" placeholder="Search users..." autoFocus />}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewChatOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStartDirect} disabled={!dmTarget || startingChat}>
            {startingChat ? 'Starting...' : 'Start Chat'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── New Group dialog (admin only) ── */}
      <Dialog open={newGroupOpen} onClose={() => setNewGroupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Group</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label="Group name" value={groupName} onChange={(e) => setGroupName(e.target.value)} autoFocus />
            <Autocomplete
              multiple
              options={groupMembersAvailable}
              getOptionLabel={(o) => o.name || ''}
              value={groupMembers}
              onChange={(_, val) => setGroupMembers(val)}
              renderInput={(params) => <TextField {...params} label="Add members" placeholder="Search users..." />}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewGroupOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateGroup} disabled={!groupName.trim() || creatingGroup}>
            {creatingGroup ? 'Creating...' : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Manage group members dialog ── */}
      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Manage Members</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1, mb: 2 }}>
            {(selected?.participants || []).map((p) => (
              <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: (ROLE_COLORS[p.role] || '#888') + '30', color: ROLE_COLORS[p.role] || '#888' }}>
                  {p.name?.charAt(0)}
                </Avatar>
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{p.name}</Typography>
                {p.participant_role === 'admin' && <Chip label="Admin" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />}
                {p.id !== user?.id && (
                  <Tooltip title="Remove from group">
                    <IconButton size="small" onClick={() => handleRemoveMember(p.id)}>
                      <PersonRemoveIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Autocomplete
            options={addMemberAvailable}
            getOptionLabel={(o) => o.name || ''}
            value={addMemberPick}
            onChange={(_, val) => setAddMemberPick(val)}
            renderInput={(params) => <TextField {...params} label="Add a member" placeholder="Search users..." />}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setManageOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleAddMember} disabled={!addMemberPick}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}